/**
 * Kit turntable (`?scene=turntable`; the `?scene=kit` of 05 §5 rule 6): every prop kind, every soldier look and every
 * machine, each turning on its own spot in sixteen steps, under the real pipeline. Models are
 * packed onto pages of floor the size of the game's view; pages turn over by themselves.
 *
 *   &biome=arctic|desert|temperate  &tod=dawn|day|dusk|night
 *   &kind=<name>   one model alone, closer (any prop kind, or ranger rifleman elite officer
 *                  flamethrower rocket captive prisoner scientist pilot defector general courier
 *                  miniBunker miniBunkerDormant minitank osprey supplyPod canopy edgeCrest;
 *                  the vehicles of ROUND4 §1 under their ENEMY kind - truck railEngine launcherTruck,
 *                  each with its wreck; the moving `staffCar` is the `staffCar` prop's own model;
 *                  rangerGhillie, the ranger in his ghillie wrap (ROUND7); dog and dogSit, the tracker
 *                  dog (ROUND8), shelved beside the men; wallRow, a run laid in 1.5 m
 *                  pieces as the generator lays it, and wallBreach, the same with two pieces blown -
 *                  `wallWreck` is one blown piece alone;
 *                  truckEmpty, the lorry with nobody aboard, against `truck` which is carrying men;
 *                  bridgeWreck and bridgeWreckDry, the fallen span in water and on ice / dry ground;
 *                  `<kind>Wreck` for any kind with a wreck of its own, e.g. relayMastWreck or
 *                  bunkerWreck; `<kind>Off` for a switched kind's other state: breakerPanelOff
 *                  generatorHutOff strikeBeaconOff; `<kind>Manned` for a concrete emplacement with
 *                  its crew behind it: bunkerManned turretBunkerManned casemateManned - compare
 *                  each with the plain kind, which is the same emplacement standing empty)
 *   &page=2        hold one page      &ppm=18 &pitch=45
 *   &freeze=1&t=3  a fixed moment: step and page follow from `t`, so a saved frame can be repeated
 */

import {
  Box3, Color, DirectionalLight, Group, HemisphereLight, type Material, Mesh, type Object3D, Scene,
  type WebGLRenderer,
} from 'three';
import { PixelCamera } from '../render/camera/PixelCamera';
import { Dog } from '../render/characters/dog';
import { targetLook } from '../render/characters/model';
import { Soldier, type SoldierLook } from '../render/characters/soldier';
import { edgeCrest } from '../render/kit/edges';
import { EMPLACEMENT_TELLS } from '../render/kit/emplacements';
import type { KitBuilder } from '../render/kit/flora';
import {
  ANTENNA_RAISED_Y, ANTENNA_RETRACTED_Y, miniBunkerAntenna, miniBunkerBody, miniBunkerSlot, minitankBody,
} from '../render/kit/machines';
import type { MeshBuilder } from '../render/kit/MeshBuilder';
import { packShelves, pageCount, type Placement } from '../render/kit/shelves';
import { brokenSpan, SPAN_IN_WATER, SPAN_ON_ICE } from '../render/kit/spans';
import { canopy, supplyPod } from '../render/kit/structures';
import { buildOsprey } from '../render/kit/vehicles';
import { wallRowSample } from '../render/kit/walls';
import { buildWreck, wreckTurnsWithProp } from '../render/kit/wrecks';
import {
  createEmissiveMaterial, createGradientMap, createToonMaterial, OUTLINE, sharedUniforms,
} from '../render/materials/toon';
import { type BiomeName, getGrade, Palette, type TimeOfDay } from '../render/palette';
import { RenderPipeline } from '../render/pipeline/RenderPipeline';
import { buildTerrain } from '../render/terrain/Terrain';
import { MACHINE_MODELS } from '../render/world/machineModels';
import { buildPropModel, initialSwitch, isRunKind } from '../render/world/propModels';
import { glowBoost } from '../render/world/PropGlow';
import { isVehicle } from '../sim/enemies/specials';
import { PROPS } from '../sim/props';
import { Pcg32 } from '../sim/rng';
import type { EnemyKind, PropKind } from '../sim/types';
import { saveFrame } from './frameSave';
import { num, pick, query } from './params';

/** Seconds a model holds each of its sixteen headings, and a page stays up (one full turn). */
const STEP_SECONDS = 0.5;
const STEPS = 16;
const PAGE_SECONDS = STEP_SECONDS * STEPS;
const RUN_LENGTH = 8;

const LOOKS: Record<string, SoldierLook> = {
  ranger: { side: 'ranger', skin: 'skinB', headgear: 'helmet', pack: true },
  rangerGhillie: { side: 'ranger', skin: 'skinB', headgear: 'helmet', pack: true, ghillie: true },
  rifleman: { side: 'enemy', skin: 'skinA', headgear: 'helmet', pack: false },
  elite: { side: 'enemy', skin: 'skinA', headgear: 'beret', pack: false },
  officer: { side: 'officer', skin: 'skinC', headgear: 'cap', pack: false },
  flamethrower: { side: 'enemy', skin: 'skinA', headgear: 'helmet', pack: false, kit: 'flamethrower' },
  rocket: { side: 'enemy', skin: 'skinB', headgear: 'helmet', pack: false, kit: 'rocket' },
  captive: { side: 'captive', skin: 'skinB', headgear: 'none', pack: false, rifle: false },
  prisoner: { side: 'officer', skin: 'skinC', headgear: 'cap', pack: false, rifle: false },
  // The expansion's people (10 §2), and the two marked men: the general of mission 29, the courier of 26.
  scientist: { side: 'scientist', skin: 'skinA', headgear: 'none', pack: false, rifle: false },
  pilot: { side: 'pilot', skin: 'skinB', headgear: 'flightHelmet', pack: false, rifle: false },
  defector: { side: 'defector', skin: 'skinA', headgear: 'cap', pack: false, rifle: false },
  general: targetLook({ side: 'officer', skin: 'skinA', headgear: 'cap', pack: false }, 'killTarget'),
  courier: targetLook({ side: 'enemy', skin: 'skinC', headgear: 'helmet', pack: false }, 'lootTarget'),
};

interface Item {
  name: string;
  radius: number;
  object: Object3D;
  /** Face heading `angle` (sim convention: 0 = south, toward the camera). */
  turn(angle: number): void;
}

export function startTurntable(canvas: HTMLCanvasElement, renderer: WebGLRenderer): void {
  const biome = pick<BiomeName>('biome', ['arctic', 'desert', 'temperate'], 'temperate');
  const tod = pick<TimeOfDay>('tod', ['dawn', 'day', 'dusk', 'night'], 'day');
  const only = query.get('kind');
  const palette = new Palette(biome);
  const grade = getGrade(tod);
  const gradient = createGradientMap();
  const scene = new Scene();
  const camera = new PixelCamera(num('ppm', only ? 30 : 18), num('pitch', 45));

  const sun = new DirectionalLight(new Color(grade.sunColor), grade.sunIntensity);
  sun.position.set(grade.sunDir[0] * 80, grade.sunDir[1] * 80, grade.sunDir[2] * 80);
  sun.castShadow = true;
  sun.shadow.mapSize.set(2048, 2048);
  Object.assign(sun.shadow.camera, { left: -26, right: 26, top: 26, bottom: -26, near: 10, far: 200 });
  sun.shadow.bias = -0.0006;
  sun.shadow.normalBias = 0.04;
  const fill = new HemisphereLight(new Color(grade.skyColor), new Color(grade.groundColor), grade.hemiIntensity);
  scene.add(sun, sun.target, fill);
  scene.add(buildTerrain({
    originX: -32, originZ: -26, sizeX: 64, sizeZ: 52,
    heightAt: () => 0,
    materialAt: () => [1, 0, 0, 0],
    ramps: ['ground', 'groundAlt', 'dirt', 'rock'],
    baseSteps: biome === 'arctic' ? [3, 2, 1, 1] : [2, 1, 1, 1],
  }, palette, gradient).ground);

  const propMaterial = createToonMaterial({ gradientMap: gradient, outline: OUTLINE.prop });
  const enemyMaterial = createToonMaterial({ gradientMap: gradient, outline: OUTLINE.character });
  const playerMaterial = createToonMaterial({ gradientMap: gradient, outline: OUTLINE.player });
  const items: Item[] = [];
  const soldiers: Soldier[] = [];

  /** A rigid model: its turning circle is read off its own bounding box. */
  const rigid = (name: string, object: Object3D): void => {
    const box = new Box3().setFromObject(object);
    const radius = Math.hypot(Math.max(-box.min.x, box.max.x), Math.max(-box.min.z, box.max.z));
    items.push({ name, radius: Math.max(0.6, radius), object, turn: (angle) => { object.rotation.y = angle; } });
  };
  const mesh = (mb: MeshBuilder, material: Material): Mesh => {
    const m = new Mesh(mb.build(), material);
    m.castShadow = true;
    m.receiveShadow = true;
    return m;
  };
  const built = (builder: KitBuilder, material: Material, seed: number): Mesh =>
    mesh(builder(palette, new Pcg32(seed, 5)), material);

  (Object.keys(PROPS) as PropKind[]).forEach((kind, i) => {
    const def = { id: i + 1, kind, x: 0, z: 0, yaw: 0, ...(isRunKind(kind) ? { length: RUN_LENGTH } : {}) };
    rigid(kind, mesh(buildPropModel(def, palette, new Pcg32(i + 1, 5)), propMaterial));
    // Beside each thing, what it turns into: its other state, and the wreck it leaves if it has one of its own.
    if (initialSwitch(kind) !== null) {
      rigid(`${kind}Off`, mesh(buildPropModel(def, palette, new Pcg32(i + 1, 5), 'off'), propMaterial));
    }
    if (wreckTurnsWithProp(kind)) {
      rigid(`${kind}Wreck`, mesh(buildWreck(kind, palette, new Pcg32(i + 1, 9), 2, 2), propMaterial));
    }
  });
  for (const [name, look] of Object.entries(LOOKS)) {
    const soldier = new Soldier(palette, look.side === 'ranger' ? playerMaterial : enemyMaterial, look);
    soldiers.push(soldier);
    items.push({ name, radius: 0.9, object: soldier.mesh, turn: (angle) => { soldier.state.facing = angle; } });
  }
  // The tracker dog (ROUND8 §3), on the shelf after the men so its size can be judged against theirs.
  const dogs = (['stand', 'sit'] as const).map((motion) => {
    const dog = new Dog(palette, enemyMaterial);
    dog.state.motion = motion;
    items.push({ name: motion === 'stand' ? 'dog' : 'dogSit', radius: 0.7, object: dog.mesh, turn: (angle) => {
      dog.state.facing = angle;
    } });
    return dog;
  });
  // A manned concrete emplacement, beside the empty one the prop table already shows (ROUND4 §3).
  const glowMaterial = createEmissiveMaterial(gradient, glowBoost(tod));
  for (const kind of Object.keys(EMPLACEMENT_TELLS) as PropKind[]) {
    const tell = EMPLACEMENT_TELLS[kind];
    if (!tell) continue;
    const group = new Group();
    group.add(mesh(buildPropModel({ id: 1, kind, x: 0, z: 0, yaw: 0 }, palette, new Pcg32(11, 5)), propMaterial));
    group.add(mesh(tell.slot(palette, new Pcg32(11, 5)), glowMaterial));
    if (tell.gun) {
      const gun = mesh(tell.gun(palette, new Pcg32(11, 5)), propMaterial);
      gun.position.set(tell.pivot[0], tell.pivot[1], tell.pivot[2]);
      group.add(gun);
    }
    rigid(`${kind}Manned`, group);
  }
  // Live and dormant mini-bunkers: antenna up and the sensor slot lit, against both down and dark.
  const bunker = built(miniBunkerBody, enemyMaterial, 7);
  const antenna = built(miniBunkerAntenna, enemyMaterial, 7);
  antenna.position.y = ANTENNA_RAISED_Y;
  bunker.add(antenna, built(miniBunkerSlot, glowMaterial, 7));
  rigid('miniBunker', bunker);
  const dormant = built(miniBunkerBody, enemyMaterial, 7);
  const stowed = built(miniBunkerAntenna, enemyMaterial, 7);
  stowed.position.y = ANTENNA_RETRACTED_Y;
  dormant.add(stowed);
  rigid('miniBunkerDormant', dormant);
  rigid('minitank', built(minitankBody, enemyMaterial, 7));
  // The vehicles (ROUND4 §1): enemies, not props, so they are listed under their own kind - with the
  // headlamps lit, and the wreck each leaves. `staffCar` is skipped: the prop of that name IS its model.
  for (const kind of Object.keys(MACHINE_MODELS) as EnemyKind[]) {
    const model = MACHINE_MODELS[kind];
    if (!model || !isVehicle(kind) || kind in PROPS) continue;
    const lit = new Group();
    lit.add(built(model.body, enemyMaterial, 7));
    if (model.lamps) lit.add(built(model.lamps, glowMaterial, 7));
    rigid(kind, lit);
    rigid(`${kind}Wreck`, built(model.wreck, enemyMaterial, 9));
    if (model.unladen) rigid(`${kind}Empty`, built(model.unladen, enemyMaterial, 7));
  }
  // The span that fell (ROUND4 §1): the deck in the water, and the same wreck on ice or dry ground.
  for (const [name, site] of [['bridgeWreck', SPAN_IN_WATER], ['bridgeWreckDry', SPAN_ON_ICE]] as const) {
    rigid(name, mesh(brokenSpan(palette, new Pcg32(9, 9), 12, site), propMaterial));
  }
  // One piece of the band that rings the map's edge (ROUND4 item 6); the biome decides what it is.
  rigid('edgeCrest', built(edgeCrest, propMaterial, 4));
  // Walls in short pieces must draw as ONE wall, and a blown piece as a breach (ROUND7 §3).
  rigid('wallRow', mesh(wallRowSample(palette, new Pcg32(6, 5), 6, 1.5, []), propMaterial));
  rigid('wallBreach', mesh(wallRowSample(palette, new Pcg32(6, 5), 6, 1.5, [2, 3]), propMaterial));
  rigid('supplyPod', built(supplyPod, propMaterial, 3));
  rigid('canopy', built(canopy, propMaterial, 5));
  const osprey = buildOsprey(palette, propMaterial);
  rigid('osprey', osprey.root);

  // An unknown `kind` shows everything rather than an empty floor.
  const picked = items.filter((item) => item.name === only);
  const shown = picked.length > 0 ? picked : items;
  for (const item of items) item.object.visible = false;
  const placements = new Map<string, Placement>(packShelves(shown).map((p) => [p.name, p]));
  for (const item of shown) scene.add(item.object);
  const pages = Math.max(1, pageCount([...placements.values()]));

  const pipeline = new RenderPipeline(renderer, palette.buildLut(), palette.color('ink.0'));
  pipeline.setGrade({ ...grade, hazeAmount: 0 }, palette.haze);
  pipeline.setPlayerRim(tod === 'night' ? palette.hex('paper.0') : null);
  const dev = document.getElementById('dev');
  const frozen = query.get('freeze') === '1';
  const heldPage = num('page', -1);
  const start = performance.now();
  let last = start;
  let listed = -1;

  function draw(now: number): void {
    const time = frozen ? num('t', 0) : (now - start) / 1000;
    const dt = frozen ? 0 : Math.min(0.1, (now - last) / 1000);
    last = now;
    sharedUniforms.uTime.value = time;
    const angle = (Math.floor(time / STEP_SECONDS) % STEPS) * ((Math.PI * 2) / STEPS);
    const page = heldPage >= 0 ? Math.min(pages - 1, Math.floor(heldPage)) : Math.floor(time / PAGE_SECONDS) % pages;
    camera.update();
    for (const item of shown) {
      const at = placements.get(item.name);
      item.object.visible = at !== undefined && at.page === page;
      if (!at || at.page !== page) continue;
      item.object.position.set(at.x, 0, at.z);
      item.turn(angle);
    }
    // Soldiers and dogs place themselves from their state (texel-locked), so hand them the same spot.
    for (const soldier of [...soldiers, ...dogs]) {
      soldier.state.position.copy(soldier.mesh.position);
      if (soldier.mesh.visible) soldier.update(time, camera);
    }
    osprey.update(time, dt);
    renderer.info.reset();
    pipeline.render(scene, camera);
    if (dev && listed !== page) {
      listed = page;
      const names = shown.filter((item) => placements.get(item.name)?.page === page).map((item) => item.name);
      dev.textContent = `kit ${biome} ${tod}  page ${page + 1}/${pages}  ${names.join(' ')}`;
    }
  }
  function frame(now: number): void {
    draw(now);
    requestAnimationFrame(frame);
  }
  requestAnimationFrame(frame);

  Object.assign(window, {
    __game: {
      /** PNG data URL of the canvas, drawn afresh first so the buffer is valid. */
      capture: () => {
        draw(performance.now());
        return canvas.toDataURL('image/png');
      },
      info: () => ({
        biome, tod, pages, items: shown.map((item) => ({ name: item.name, ...placements.get(item.name) })),
      }),
      save: (name: string) => saveFrame(pipeline, scene, camera, name),
    },
  });
}
